import { HoverIcon, PropertyIcon } from './IconBar';
import type { HoverActionIconProps, PropertyIconProps } from 'owa-mail-list-item-shared';
/* eslint-disable-next-line @typescript-eslint/no-restricted-imports  -- (https://aka.ms/OWALintWiki)
 * Baseline. Do not copy and paste"
 *	> '../index' import is restricted from being used. */
import { lazyGetFlagContextMenu } from '../index';
import { DirectionalHint } from '@fluentui/react/lib/Callout';
import classnames from 'owa-classnames';
import { observer } from 'owa-mobx-react';
import { getDensityModeString } from 'owa-fabric-theme';
import { resetFocus } from 'owa-mail-focus-manager';
import { hideMailItemContextMenu } from 'owa-mail-list-actions/lib/actions/itemContextMenuActions';
import getPropertyIcon from 'owa-mail-list-actions/lib/utils/conversationProperty/getPropertyIcon';
import PropertyIcons from 'owa-mail-list-actions/lib/utils/conversationProperty/PropertyIcons';
import getListViewStore from 'owa-mail-list-store/lib/store/Store';
import { tryGetMailboxInfoFromRowKeysOrIdsIfUnifiedInboxOrDefault } from 'owa-mail-list-store';
import { lazyToggleRowReadState } from 'owa-mail-mark-read-actions';
import { getMoveToPropertiesForContextMenu } from 'owa-mail-movetofolder-view';
import { lazyShowMoveToFolderDialogV9 } from 'owa-mail-folder-moveto-dialog';
import { MoveToFolderScenario } from 'owa-mail-moveto-control-types';
import { getFolderIdOrUnifiedInboxFolderId } from 'owa-unified-inbox-data-utils/lib/getFolderIdOrUnifiedInboxFolderId';
import {
    lazyToggleRowPinnedState,
    lazyToggleRowsFlagState,
    lazyMoveMailListRows,
} from 'owa-mail-triage-action';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import { type SmimeType } from 'smime-types';
import getIconForSmimeType from 'owa-smime/lib/utils/getIconForSmimeType';
import { getBigHoverAction } from 'owa-surface-actions/lib/utils/getBigHoverAction';
import React from 'react';
import {
    archiveRow,
    deleteRow,
    addPropertyIconToIconBar,
    getHoverActionIcon,
    doesFolderOrViewSupportHoverAction,
    findIconKeyIntersectionSet,
    shouldHideHoverIconWhenIconBarRight,
    useIsTypeRampUpdatesEnabled,
} from 'owa-mail-list-item-shared';
import type { IContextualMenuProps } from '@fluentui/react/lib/ContextualMenu';
import { getMailMenuItemShouldShow } from 'owa-mail-filterable-menu-behavior';
import type { HoverActionKey } from 'owa-outlook-service-option-store';
import type ImportanceType from 'owa-service/lib/contract/ImportanceType';
import { startAnimationOrHandleActionAsNeeded } from 'owa-mail-list-item-animation-store';
import {
    lazySaveShowSettingsHoverAction,
    getHoverSurfaceAction,
    getStore as getHoverSurfaceActionStore,
} from 'owa-surface-actions-option';
import getMailboxInfo from 'owa-mail-mailboxinfo/lib/getMailboxInfo';
import { isUnifiedInboxMailboxInfo } from 'owa-client-types/lib/UnifiedInboxMailboxInfo';
import '../webComponents/iconBar/MailListIconBar';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';

// Remark: We have to share css file with MailListItem since we need to trigger child hover selectors
// off of the MailListItem container css class
import {
    hoverActionIcon,
    icon,
    iconBarContainer,
    iconDisplay,
    iconNoDisplay,
    alwaysShowPropertyIcons,
    iconHide,
    propertyIcon,
    iconContainerNew,
    iconBarLeftMargin,
    iconInIconBar,
    applyDefaultIconColor,
    full,
    denseless,
    iconBarNoLeftMargin,
} from 'owa-mail-listitem-styles/lib/scss/IconBar.scss';
import {
    mailListItemIsPinned,
    pinFilledColor,
    visibilityHidden,
} from 'owa-mail-listitem-styles/lib/scss/MailListItemIconStyles.scss';
import { flagFilledColor } from 'owa-mail-listitem-styles/lib/scss/FlagIconStyles.scss';
import { lazyMountAndShowFullOptions } from 'owa-options-view';
import { isReadingPanePositionRight } from 'owa-mail-layout/lib/selectors/readingPanePosition';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import { type LoadMoreFoldersActionSource } from 'owa-folders-constants';
import type { MailboxType } from 'owa-graph-schema';

const hoverActionSource = 'Hover';

export interface MailListItemIconBarProps {
    supportsHoverIcons?: boolean;
    neverShowHoverIcons?: boolean;
    neverShowPropertyIcons?: boolean;
    tableViewId: string;
    rowKey: string;
    isFlagged: boolean;
    isComplete: boolean;
    isPinned: boolean;
    isInNotesFolder: boolean;
    isInArchiveFolder: boolean;
    isInOutboxFolder: boolean;
    canDelete: boolean;
    unreadCount: number;
    canArchive: boolean;
    canPin: boolean;
    itemClassIcon: PropertyIcons;
    importance: ImportanceType;
    smimeType: SmimeType;
    hasAttachment: boolean;
    hasSharepointLink: boolean;
    effectiveMentioned: boolean;
    isSnoozed: boolean;
    isTaggedForBigScreen: boolean;
    isSingleLine: boolean;
    supportsFlagging: boolean;
    supportsMove: boolean;
    supportsPinning: boolean;
    isReminderSet: boolean;
    hideImportancePropertyIcon: boolean;
    hideFlagPropertyIcon: boolean;
    containerClassName?: string;
}

/*
Renders the mail list icon bar for both action icons and property icons
*/
const MailListItemIconBar: React.FunctionComponent<MailListItemIconBarProps> = observer(
    function MailListItemIconBar(props: MailListItemIconBarProps) {
        const {
            supportsHoverIcons,
            neverShowHoverIcons,
            neverShowPropertyIcons,
            tableViewId,
            rowKey,
            isFlagged,
            isComplete,
            isPinned,
            isInNotesFolder,
            isInArchiveFolder,
            isInOutboxFolder,
            canDelete,
            unreadCount,
            canArchive,
            canPin,
            itemClassIcon,
            importance,
            smimeType,
            hasAttachment,
            hasSharepointLink,
            effectiveMentioned,
            isSnoozed,
            isTaggedForBigScreen,
            isSingleLine,
            supportsFlagging,
            supportsMove,
            supportsPinning,
            isReminderSet,
            hideImportancePropertyIcon,
            hideFlagPropertyIcon,
            containerClassName,
        } = props;

        const tableView = getListViewStore().tableViews.get(tableViewId);
        const mailboxInfo = getMailboxInfo(tableView);
        const densityModeString = getDensityModeString();
        const isReadingPaneRight = isReadingPanePositionRight();
        const isRPDenselessEnabled = useIsTypeRampUpdatesEnabled();
        // In denseless mode: icons are 20px everywhere, except MCL + compact = 16px
        const isMCLCompact = isSingleLine && densityModeString === 'compact';
        const useDenselessIcons = isRPDenselessEnabled && !isMCLCompact;
        // For hover icons, pass 'full' density to trigger the .full class (20px),
        // except MCL + compact which keeps original density (16px)
        const hoverIconDensity = isRPDenselessEnabled
            ? isMCLCompact
                ? densityModeString
                : 'full'
            : densityModeString;

        const shouldShowHoverActionsInReceivedColumn = isReadingPaneRight && isSingleLine;
        const hoverFlagIndexRef = React.useRef(-1);
        const hoverPinIndexRef = React.useRef(-1);
        const propFlagIndexRef = React.useRef(-1);
        const propPinIndexRef = React.useRef(-1);
        const isReminderSupported = supportsFlagging && isFlagged && !isComplete;
        const shouldShowSettingsHoverButton = getHoverSurfaceActionStore().showSettingsHoverAction;

        const onDismissMoveToMenu = React.useCallback(() => {
            hideMailItemContextMenu();
            resetFocus('MailListMoveMenuDismissed');
        }, []);

        const moveRow = React.useCallback((): IContextualMenuProps | undefined => {
            return getMoveToPropertiesForContextMenu(
                tableViewId,
                mailboxInfo,
                onDismissMoveToMenu,
                hoverActionSource,
                getMailMenuItemShouldShow,
                DirectionalHint.bottomLeftEdge,
                [rowKey]
            );
        }, [tableViewId, onDismissMoveToMenu, rowKey]);

        const toggleMarkAsReadRow = React.useCallback(() => {
            lazyToggleRowReadState.importAndExecute(rowKey, tableViewId, hoverActionSource);
            // Reset focus after the stitch to mark the item as read is raised
            resetFocus('MailListToggleMarkAsRead');
        }, [rowKey, tableViewId]);

        const togglePinnedStateRow = React.useCallback(() => {
            lazyToggleRowPinnedState.importAndExecute(rowKey, tableViewId, hoverActionSource);
            // Reset focus after the stitch to pin the item is raised
            resetFocus('MailListItemIconBarPinned');
        }, [rowKey, tableViewId]);

        const flaggedOnContextMenu = React.useCallback(
            (
                evt:
                    | React.MouseEvent<HTMLAnchorElement | HTMLButtonElement | unknown, MouseEvent>
                    | React.TouchEvent<HTMLAnchorElement | HTMLButtonElement | unknown>
            ) => {
                evt.stopPropagation();
                evt.persist();
                lazyGetFlagContextMenu.importAndExecute(
                    rowKey,
                    tableViewId,
                    '' /* nodeId */,
                    hoverActionSource,
                    evt as React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>
                );
                evt.preventDefault();
            },
            [rowKey, tableViewId]
        );

        const toggleFlaggedStateRow = React.useCallback(() => {
            lazyToggleRowsFlagState.importAndExecute([rowKey], tableViewId, hoverActionSource);
            // Reset focus after the stitch to flag the item is raised
            resetFocus('MailListItemIconBarFlagged');
        }, [rowKey, tableViewId]);

        const shouldHideHoverIconWhenIconBarRightCallback = React.useCallback(
            (hoverIconKey: string) => {
                return shouldHideHoverIconWhenIconBarRight(
                    hoverIconKey,
                    isFlagged,
                    isComplete,
                    isPinned,
                    false /* isItemPart */
                );
            },
            [isFlagged, isComplete, isPinned]
        );

        const hoverActionKeys = useComputedValue((): HoverActionKey[] => {
            let hoverSurfaceActionKeys = getHoverSurfaceActionStore().hoverSurfaceActions;
            if (isInNotesFolder || isInOutboxFolder) {
                return ['Delete'];
            } else if (hoverSurfaceActionKeys === null) {
                hoverSurfaceActionKeys = getHoverSurfaceAction();
            }

            const unsupportedActionKeys: HoverActionKey[] = [];
            const supportedActionKeys: HoverActionKey[] = [];
            for (const actionKey of hoverSurfaceActionKeys) {
                const key = doesFolderOrViewSupportHoverAction(
                    actionKey,
                    canDelete,
                    canArchive,
                    supportsMove,
                    supportsFlagging,
                    supportsPinning,
                    canPin
                )
                    ? actionKey
                    : 'None';

                if (key === 'None') {
                    unsupportedActionKeys.push(key);
                } else {
                    supportedActionKeys.push(key);
                }
            }
            // We want unsupported actions to be at the front of the list to get the correct alignment between prop icon and hover icon
            const filteredActionKeys = unsupportedActionKeys.concat(supportedActionKeys);

            if (shouldShowSettingsHoverButton) {
                filteredActionKeys[0] = 'Settings';
            }

            return filteredActionKeys;
        }, [
            shouldShowSettingsHoverButton,
            canDelete,
            canArchive,
            supportsMove,
            supportsFlagging,
            supportsPinning,
            canPin,
            isInNotesFolder,
            isInOutboxFolder,
        ]);

        const bigHoverAction = getBigHoverAction(hoverActionKeys);
        const hoverActionIcons = useComputedValue((): HoverActionIconProps[] => {
            const hoverActions: HoverActionIconProps[] = [];

            hoverFlagIndexRef.current = -1;
            hoverPinIndexRef.current = -1;
            for (let i = 0; i < hoverActionKeys.length; i++) {
                const action = hoverActionKeys[i];
                // in SCL, if BigHoverAction is present, shift hover actions to right
                const iconIndex = !isSingleLine && bigHoverAction != 'None' ? i + 1 : i;
                const hoverActionIconProps = getHoverActionIcon(
                    action,
                    isFlagged,
                    isComplete,
                    isPinned,
                    unreadCount
                );
                hoverActionIconProps.action = action;
                if (action === 'Settings') {
                    hoverActions[i] = {
                        ...hoverActionIconProps,
                        onClickCommand: () => {
                            lazySaveShowSettingsHoverAction.importAndExecute().then(() => {
                                lazyMountAndShowFullOptions.importAndExecute(
                                    'mail',
                                    'surfaceActions'
                                );
                            });
                        },
                    };
                } else if (action == 'Delete' && (isSingleLine || bigHoverAction !== action)) {
                    hoverActions[iconIndex] = {
                        ...hoverActionIconProps,
                        onClickCommand: () => deleteRow(rowKey, tableViewId, hoverActionSource),
                    };
                } else if (action == 'Archive' && (isSingleLine || bigHoverAction !== action)) {
                    hoverActions[iconIndex] = {
                        ...hoverActionIconProps,
                        onClickCommand: () => {
                            const handleArchive = () => {
                                archiveRow(rowKey, tableViewId, hoverActionSource);
                            };
                            startAnimationOrHandleActionAsNeeded(
                                'Archive',
                                [rowKey],
                                hoverActionSource,
                                false /*isActionTakenFromDialog*/,
                                handleArchive
                            );
                        },
                    };
                } else if (action == 'Move') {
                    // If in Unified Inbox, open the Move To dialog directly
                    if (isUnifiedInboxMailboxInfo(mailboxInfo)) {
                        hoverActions[iconIndex] = {
                            ...hoverActionIconProps,
                            onClickCommand: () => {
                                // Get the actual mailboxInfo and folderId of the item to move
                                const unifiedInboxFolderId = tableView?.tableQuery?.folderId;
                                const actualMailboxInfo =
                                    tryGetMailboxInfoFromRowKeysOrIdsIfUnifiedInboxOrDefault(
                                        getModuleContextMailboxInfo(),
                                        [rowKey],
                                        tableView
                                    );
                                const actualSourceFolderId = getFolderIdOrUnifiedInboxFolderId(
                                    unifiedInboxFolderId,
                                    actualMailboxInfo
                                );

                                const selectorProps = {
                                    sourceFolderId: actualSourceFolderId,
                                    supportedMailBoxTypes: [
                                        'UserMailbox',
                                        'ArchiveMailbox',
                                        'SharedMailbox',
                                    ] as MailboxType[],
                                    onFolderClick: (destinationFolderId: string) => {
                                        lazyMoveMailListRows.importAndExecute(
                                            [rowKey],
                                            destinationFolderId,
                                            tableViewId,
                                            hoverActionSource
                                        );
                                    },
                                    excludeSearchFolders: true,
                                    excludeNotesFolder: true,
                                    includeAllAccounts: true,
                                    loadMoreActionSource: 'ContextMenu',
                                };
                                lazyShowMoveToFolderDialogV9.importAndExecute({
                                    targetWindow: window,
                                    mailboxInfo: actualMailboxInfo,
                                    scenario: MoveToFolderScenario.MoveTo,
                                    source: 'HoverAction',
                                    selectorProps: { ...selectorProps },
                                });
                            },
                        };
                    } else {
                        hoverActions[iconIndex] = { ...hoverActionIconProps, menuProps: moveRow() };
                    }
                } else if (action == 'ReadUnread') {
                    hoverActions[iconIndex] = {
                        ...hoverActionIconProps,
                        onClickCommand: toggleMarkAsReadRow,
                    };
                } else if (action == 'FlagUnflag') {
                    hoverFlagIndexRef.current = iconIndex;
                    hoverActions[iconIndex] = {
                        ...hoverActionIconProps,
                        onClickCommand: toggleFlaggedStateRow,
                        onContextMenu: flaggedOnContextMenu,
                    };
                } else if (action == 'PinUnpin') {
                    hoverPinIndexRef.current = iconIndex;
                    hoverActions[iconIndex] = {
                        ...hoverActionIconProps,
                        onClickCommand: togglePinnedStateRow,
                    };
                }
            }

            return hoverActions;
        }, [
            isInNotesFolder,
            isInArchiveFolder,
            isInOutboxFolder,
            rowKey,
            canArchive,
            tableViewId,
            canDelete,
            unreadCount,
            isPinned,
            canPin,
            supportsMove,
            supportsFlagging,
            supportsPinning,
            isFlagged,
            isComplete,
            isSingleLine,
            hoverActionKeys,
        ]);

        const propertyIcons = useComputedValue((): PropertyIconProps[] => {
            propFlagIndexRef.current = -1;
            propPinIndexRef.current = -1;
            // for SCL we show 3 icons b/c delete is bigHoverAction
            const MAX_PROPERTY_ICONS = 4;
            const propertyIconProps: PropertyIconProps[] = Array.from(
                { length: MAX_PROPERTY_ICONS },
                (_, index) => ({
                    key: `placeholder-${index}`,
                    iconName: '',
                })
            );
            const spotsToFill = Array.from({ length: MAX_PROPERTY_ICONS }, () => false);
            const remainingSpots = { value: MAX_PROPERTY_ICONS };

            // Flag or Complete icon
            const addFlagPropertyIconIfSupported = () => {
                if (supportsFlagging && (isFlagged || isComplete)) {
                    let flagPropertyIcon: PropertyIconProps = {
                        iconName: 'None',
                        key: 'flagIcon',
                        id: PropertyIcons.None,
                    };
                    if (isFlagged && !isComplete) {
                        flagPropertyIcon = {
                            key: 'flagIcon',
                            id: PropertyIcons.Flagged,
                            iconClasses: flagFilledColor,
                            iconName: getPropertyIcon(PropertyIcons.Flagged).iconName,
                        };
                    } else if (isComplete) {
                        flagPropertyIcon = {
                            key: 'completeIcon',
                            id: PropertyIcons.Completed,
                            iconName: getPropertyIcon(PropertyIcons.Completed).iconName,
                        };
                    }

                    addPropertyIconToIconBar(
                        propertyIconProps,
                        spotsToFill,
                        remainingSpots,
                        flagPropertyIcon,
                        hoverFlagIndexRef.current
                    );
                }
            };

            // Pin icon if pinned
            const addPinPropertyIconIfSupported = () => {
                if (supportsPinning && isPinned) {
                    addPropertyIconToIconBar(
                        propertyIconProps,
                        spotsToFill,
                        remainingSpots,
                        {
                            key: 'pinIcon',
                            id: PropertyIcons.Pinned,
                            iconClasses: classnames(
                                pinFilledColor,
                                (isFlagged || isComplete) && !isPinned && visibilityHidden
                            ),
                            iconName: getPropertyIcon(PropertyIcons.Pinned).iconName,
                        },
                        hoverPinIndexRef.current
                    );
                }
            };

            // add flag and pin property icons
            // if either flag or pin hover action is enabled, but not both, prioritize adding the enabled one first
            if (hoverPinIndexRef.current > -1 && hoverFlagIndexRef.current == -1) {
                addPinPropertyIconIfSupported();
                if (!hideFlagPropertyIcon) {
                    addFlagPropertyIconIfSupported();
                }
            } else {
                if (!hideFlagPropertyIcon) {
                    addFlagPropertyIconIfSupported();
                }
                addPinPropertyIconIfSupported();
            }

            // Item class icon first (meeting, note, etc.)
            if (itemClassIcon && itemClassIcon !== PropertyIcons.None && remainingSpots.value > 0) {
                addPropertyIconToIconBar(propertyIconProps, spotsToFill, remainingSpots, {
                    key: itemClassIcon.toString(),
                    id: itemClassIcon,
                    ...getPropertyIcon(itemClassIcon),
                });
            }
            // Importance icon (skip if already shown in the sorted content column)
            if (!hideImportancePropertyIcon) {
                if (importance == 'Low' && remainingSpots.value > 0) {
                    addPropertyIconToIconBar(propertyIconProps, spotsToFill, remainingSpots, {
                        key: PropertyIcons.ImportanceLow.toString(),
                        id: PropertyIcons.ImportanceLow,
                        ...getPropertyIcon(PropertyIcons.ImportanceLow),
                    });
                } else if (importance == 'High' && remainingSpots.value > 0) {
                    addPropertyIconToIconBar(propertyIconProps, spotsToFill, remainingSpots, {
                        key: PropertyIcons.ImportanceHigh.toString(),
                        id: PropertyIcons.ImportanceHigh,
                        ...getPropertyIcon(PropertyIcons.ImportanceHigh),
                    });
                }
            }
            // S/MIME icons
            if (smimeType !== 0 && remainingSpots.value > 0) {
                const smimeIcon = getIconForSmimeType(smimeType);
                const smimeIconId = smimeIcon
                    ? PropertyIcons[smimeIcon.toString() as keyof typeof PropertyIcons]
                    : null;
                smimeIconId &&
                    addPropertyIconToIconBar(propertyIconProps, spotsToFill, remainingSpots, {
                        key: smimeIconId.toString(),
                        id: smimeIconId,
                        iconName: getPropertyIcon(smimeIconId).iconName,
                    });
            }
            // Attachment icon
            if (hasAttachment && remainingSpots.value > 0) {
                addPropertyIconToIconBar(propertyIconProps, spotsToFill, remainingSpots, {
                    key: PropertyIcons.Attachment.toString(),
                    id: PropertyIcons.Attachment,
                    ...getPropertyIcon(PropertyIcons.Attachment),
                });
            } else if (hasSharepointLink && remainingSpots.value > 0) {
                addPropertyIconToIconBar(propertyIconProps, spotsToFill, remainingSpots, {
                    key: PropertyIcons.Link.toString(),
                    id: PropertyIcons.Link,
                    ...getPropertyIcon(PropertyIcons.Link),
                });
            }
            // At mention icon
            if (effectiveMentioned && remainingSpots.value > 0) {
                addPropertyIconToIconBar(propertyIconProps, spotsToFill, remainingSpots, {
                    key: PropertyIcons.GlobalMentionedMe.toString(),
                    id: PropertyIcons.GlobalMentionedMe,
                    ...getPropertyIcon(PropertyIcons.GlobalMentionedMe),
                    iconClasses: classnames(supportsPinning && isPinned && mailListItemIsPinned),
                });
            }
            // If the conversation/item has been snoozed or tagged for big screen, then show the clock icon
            if ((isSnoozed || isTaggedForBigScreen) && remainingSpots.value > 0) {
                addPropertyIconToIconBar(propertyIconProps, spotsToFill, remainingSpots, {
                    key: PropertyIcons.Snooze.toString(),
                    id: PropertyIcons.Snooze,
                    ...getPropertyIcon(PropertyIcons.Snooze),
                });
            }

            // Reminder icon
            if (isReminderSupported && isReminderSet && remainingSpots.value > 0) {
                addPropertyIconToIconBar(propertyIconProps, spotsToFill, remainingSpots, {
                    key: PropertyIcons.Reminder.toString(),
                    id: PropertyIcons.Reminder,
                    ...getPropertyIcon(PropertyIcons.Reminder),
                });
            }
            // Trim leading property icon placeholders
            let removeCount = 0;
            while (propertyIconProps.length > 0 && propertyIconProps[0].iconName === '') {
                propertyIconProps.shift();
                removeCount++;
            }
            // Property Icon indexes for flag and pin icons are based on location in hoverActionIcons array
            // Since we are removed leading placeholders, we need to adjust the indexes by the number of
            // leading placeholders removed
            if (hoverFlagIndexRef.current > -1) {
                propFlagIndexRef.current = hoverFlagIndexRef.current - removeCount;
            }
            if (hoverPinIndexRef.current > -1) {
                propPinIndexRef.current = hoverPinIndexRef.current - removeCount;
            }
            return propertyIconProps;
        }, [
            itemClassIcon,
            importance,
            smimeType,
            hasAttachment,
            hasSharepointLink,
            effectiveMentioned,
            unreadCount,
            isSnoozed,
            isTaggedForBigScreen,
            rowKey,
            isComplete,
            isFlagged,
            isPinned,
            canPin,
            supportsFlagging,
            supportsPinning,
            hoverActionKeys,
            isReminderSet,
            hideImportancePropertyIcon,
            hideFlagPropertyIcon,
        ]);

        const containerClassNames = classnames(
            iconBarContainer,
            containerClassName,
            isSingleLine && isReadingPaneRight ? iconBarNoLeftMargin : iconBarLeftMargin
        );

        // when icons are in both hover actions and property icons, it means that the hover action is enabled and the property associated with that action is true
        // only applicable for flag and pin actions.
        const iconKeyIntersectionSet = useComputedValue(() => {
            return findIconKeyIntersectionSet(
                hoverActionIcons,
                propertyIcons,
                isSingleLine,
                hoverFlagIndexRef.current,
                hoverPinIndexRef.current,
                propFlagIndexRef.current,
                propPinIndexRef.current
            );
        }, [hoverActionIcons, propertyIcons, isSingleLine]);

        const hasIntersection = iconKeyIntersectionSet && iconKeyIntersectionSet.size > 0;

        const getVisibilityClasses = React.useCallback(
            (hoverActionKey: string, isEnabled: boolean) => {
                return isEnabled
                    ? hasIntersection && shouldHideHoverIconWhenIconBarRightCallback(hoverActionKey)
                        ? classnames(iconDisplay, iconHide) // see --iconVisibilityWhenDefaultHidden
                        : iconKeyIntersectionSet?.has(hoverActionKey) && iconDisplay
                    : undefined;
            },
            [iconKeyIntersectionSet, shouldHideHoverIconWhenIconBarRightCallback]
        );

        if (!neverShowHoverIcons || !neverShowPropertyIcons) {
            return (
                <div className={containerClassNames}>
                    {!neverShowHoverIcons &&
                        hoverActionIcons.map(hoverProps => {
                            return (
                                <HoverIcon
                                    {...hoverProps}
                                    key={hoverProps.key}
                                    buttonClasses={classnames(
                                        hoverProps.buttonClasses, // Hide certain icons when the item is not hovered to prevent shifting of pinned/flagged status
                                        getVisibilityClasses(
                                            hoverProps.key,
                                            shouldShowHoverActionsInReceivedColumn
                                        )
                                    )}
                                    iconClasses={classnames(
                                        hoverActionIcon,
                                        icon,
                                        iconContainerNew
                                    )}
                                    densityModeString={hoverIconDensity}
                                />
                            );
                        })}
                    {!neverShowPropertyIcons &&
                        propertyIcons.map(propertyIconProps => {
                            return (
                                <PropertyIcon
                                    {...propertyIconProps}
                                    key={propertyIconProps.key}
                                    iconClasses={classnames(
                                        iconInIconBar,
                                        !propertyIconProps.iconClasses
                                            ? applyDefaultIconColor
                                            : undefined,
                                        !useDenselessIcons && densityModeString === 'full' && full,
                                        useDenselessIcons && denseless,
                                        propertyIconProps.iconClasses,
                                        propertyIcon,
                                        (!isReadingPaneRight || !isSingleLine) &&
                                            neverShowHoverIcons &&
                                            alwaysShowPropertyIcons, // In SCL or when RP bottom or Hidden, if neverShowHoverIcons is true, force to always show the property icon.
                                        shouldShowHoverActionsInReceivedColumn
                                            ? iconKeyIntersectionSet?.has(propertyIconProps.key)
                                                ? supportsHoverIcons
                                                    ? iconNoDisplay
                                                    : alwaysShowPropertyIcons
                                                : alwaysShowPropertyIcons
                                            : undefined,
                                        iconContainerNew
                                    )}
                                />
                            );
                        })}
                </div>
            );
        } else {
            return null;
        }
    },
    'MailListItemIconBar'
);
export default function (props: MailListItemIconBarProps): React.ReactElement {
    if (isGlobalFeatureEnabled('fwk-web-components')) {
        const {
            isSingleLine,
            neverShowHoverIcons = false,
            neverShowPropertyIcons = false,
            isInNotesFolder,
            isInOutboxFolder,
            canDelete,
            canArchive,
            supportsMove,
            supportsFlagging,
            supportsPinning,
            canPin,
            isFlagged,
            isComplete,
            isPinned,
            hideImportancePropertyIcon,
            hasAttachment,
            hasSharepointLink,
            effectiveMentioned,
            isSnoozed,
            isTaggedForBigScreen,
            isReminderSet,
            importance,
            smimeType,
            itemClassIcon,
            unreadCount,
            rowKey,
            tableViewId,
            supportsHoverIcons,
        } = props;
        return (
            <owa-mail-list-item-icon-bar
                isSingleLine={isSingleLine}
                neverShowHoverIcons={neverShowHoverIcons}
                neverShowPropertyIcons={neverShowPropertyIcons}
                isInNotesFolder={isInNotesFolder}
                isInOutboxFolder={isInOutboxFolder}
                canDelete={canDelete}
                canArchive={canArchive}
                supportsMove={supportsMove}
                supportsFlagging={supportsFlagging}
                supportsPinning={supportsPinning}
                canPin={canPin}
                isFlagged={isFlagged}
                isComplete={isComplete}
                isPinned={isPinned}
                hideImportancePropertyIcon={hideImportancePropertyIcon}
                hasAttachment={hasAttachment}
                hasSharepointLink={hasSharepointLink}
                effectiveMentioned={effectiveMentioned}
                isSnoozed={isSnoozed}
                isTaggedForBigScreen={isTaggedForBigScreen}
                isReminderSet={isReminderSet}
                importance={importance}
                smimeType={smimeType}
                itemClassIcon={itemClassIcon}
                unreadCount={unreadCount}
                rowKey={rowKey}
                tableViewId={tableViewId}
                supportsHoverIcons={supportsHoverIcons}
            ></owa-mail-list-item-icon-bar>
        );
    } else {
        return <MailListItemIconBar {...props} />;
    }
}
